import React from 'react'
import PropTypes from 'prop-types'
import Dropdown from 'react-dropdown'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import DayPicker, { DateUtils } from 'react-day-picker'
import MomentLocaleUtils from 'react-day-picker/moment'
import { injectIntl, intlShape } from 'react-intl'

import { updateAction, syncFiltersAction } from 'actions/events-filters'

function fireClick(node) {
  if (document.createEvent) {
    const evt = document.createEvent('MouseEvents')
    evt.initEvent('click', true, false)
    node.dispatchEvent(evt)
  } else if (document.createEventObject) {
    node.fireEvent('onclick')
  } else if (typeof node.onclick === 'function') {
    node.onclick()
  }
}

function stopEvent(e) {
  e.stopPropagation()
  e.nativeEvent.stopImmediatePropagation()
  return null
}

class CellarEventsFilter extends React.Component {
  constructor(props) {
    super(props)

    this.getFiltersOptions = this.getFiltersOptions.bind(this)
    this.state = {
      openDatePicker: false,
      datePickerRange: {
        from: undefined,
        to: undefined,
      },
    }
  }

  componentDidMount() {
    this.props.syncFilters()
  }

  getFiltersOptions() {
    const {
      getEvents,
      eventsFilters,
    } = this.props

    const {
      guests,
      dates,
      location,
      tag,
    } = eventsFilters

    const accumulator = {
      guests: new Set(),
      dates: new Set(),
      locations: new Set(),
      tags: new Set(),
    }

    for (const event of getEvents({
      dates,
      location,
      tag,
    })) {
      accumulator.guests.add(event.places)
    }

    for (const event of getEvents({
      guests,
      location,
      tag,
    })) {
      for (const { d } of event.dates) {
        accumulator.dates.add(d.substring(0, 10))
      }
    }

    for (const event of getEvents({
      guests,
      dates,
      tag,
    })) {
      accumulator.locations.add(`${event.location._id}____${event.location.title}`)
    }

    const tagsForEachFunction = (tagObject) => {
      accumulator.tags.add(`${tagObject.tag}____${tagObject.title}`)
    }
    for (const event of getEvents({
      guests,
      dates,
      location,
    })) {
      if (event.tags && event.tags.length) {
        event.tags.forEach(tagsForEachFunction)
      }
    }

    const guestsOptions = Array.from(
      { length: Math.max(...accumulator.guests) },
      (v, k) => k + 1
    )
    const dateOptions = Array.from(accumulator.dates)
    const locationOptions = Array.from(
      accumulator.locations,
      (v) => {
        const [value, label] = v.split('____')
        return { value, label }
      }
    )
    const tagOptions = Array.from(
      accumulator.tags,
      (v) => {
        const [value, label] = v.split('____')
        return { value, label }
      }
    )

    return {
      guestsOptions,
      dateOptions,
      locationOptions,
      tagOptions,
    }
  }

  buildResetButton(filter) {
    return (
      <button
        className="button--secondary events-filters__filter__button--reset"
        onClick={() => {
          this.props.updateFilters(filter, undefined)
        }}
        style={{
          visibility: this.props.eventsFilters[filter]
            ? ''
            : 'hidden',
        }}
      ><i className="icon--undo" /></button>
    )
  }

  buildFiltersDropdowns() {
    const {
      eventsFilters,
      updateFilters,
      intl,
    } = this.props

    const { formatMessage } = intl

    const {
      guests,
      dates,
      location,
      tag,
    } = eventsFilters

    const {
      guestsOptions,
      dateOptions,
      locationOptions,
      tagOptions,
    } = this.getFiltersOptions()

    const guestsDropdown = (<div
      className="events-filters__filter"
      id="events-filters__filter--guests"
    >
      <Dropdown
        value={String(guests || '')}
        options={guestsOptions}
        onChange={({ value }) => updateFilters('guests', value)}
        placeholder={formatMessage({ id: 'app.cellar.filters.guests' })}
      />
      {this.buildResetButton('guests')}
    </div>)

    const { from, to } = this.state.datePickerRange
    const dateDropdownValue = (dates && dates.from && dates.to)
      ? `${dates.from.toISOString().substring(0, 10)} - ${dates.to.toISOString().substring(0, 10)}`
      : undefined
    const selectedDays = [from, { from, to }]
    const dayPickerModifiers = {
      disabled: day => {
        const dayString = day.toISOString().substring(0, 10)

        if (DateUtils.isPastDay(day)) {
          return true
        }

        return !dateOptions.includes(dayString)
      },
      start: from,
      end: to,
    }

    const dateDropdown = (<div
      className="events-filters__filter"
      id="events-filters__filter--date"
    >
      <Dropdown
        value={dateDropdownValue}
        options={[]}
        onOpen={() => this.setState({ datePickerRange: {}, openDatePicker: true })}
        onClose={() => this.setState({ openDatePicker: false })}
        placeholder={formatMessage({ id: 'app.cellar.filters.dates' })}
      />
      {this.buildResetButton('dates')}
      <br />
      <div style={{ position: 'relative' }}>
        <div
          style={{
            display: this.state.openDatePicker ? '' : 'none',
            position: 'absolute',
            background: 'white',
            zIndex: 1000,
          }}
          onClick={stopEvent}
          onTouchStart={stopEvent}
          onTouchEnd={stopEvent}
          onMouseDown={stopEvent}
          onMouseUp={stopEvent}
        >
          <DayPicker
            localeUtils={MomentLocaleUtils}
            fromMonth={new Date}
            ref={ el => this.dayPicker = el }
            modifiers={dayPickerModifiers}
            selectedDays={selectedDays}
            numberOfMonths={1}
            onDayClick={(day, { disabled }) => {
              if (disabled) {
                return
              }
              const range = DateUtils.addDayToRange(day, this.state.datePickerRange)
              this.setState({ datePickerRange: range })
              if (range.from && range.to) {
                updateFilters('dates', range)
                fireClick(document)
              }
            }}
          />
        </div>
      </div>
    </div>)

    const locationDropdown = locationOptions.length
      ? (<div
        className="events-filters__filter"
        id="events-filters__filter--location"
      >
        <Dropdown
          value={location}
          options={locationOptions}
          onChange={({ value }) => updateFilters('location', value)}
          placeholder={formatMessage({ id: 'app.cellar.filters.location' })}
        />
        {this.buildResetButton('location')}
      </div>)
      : null

    const tagDropdown = tagOptions.length
      ? (<div
        className="events-filters__filter"
        id="events-filters__filter--tag"
      >
        <Dropdown
          value={tag}
          options={tagOptions}
          onChange={({ value }) => updateFilters('tag', value)}
          placeholder={formatMessage({ id: 'app.cellar.filters.tag' })}
        />
        {this.buildResetButton('tag')}
      </div>)
      : null

    return {
      guestsDropdown,
      dateDropdown,
      locationDropdown,
      tagDropdown,
    }
  }

  render() {
    const {
      guestsDropdown,
      dateDropdown,
      locationDropdown,
      tagDropdown,
    } = this.buildFiltersDropdowns()

    return (
      <div className="events-filters">
        { guestsDropdown }
        { dateDropdown }
        { locationDropdown }
        { tagDropdown }
      </div>
    )
  }
}

CellarEventsFilter.propTypes = {
  intl: intlShape.isRequired,
  eventsFilters: PropTypes.object.isRequired,
  getEvents: PropTypes.func.isRequired,
  currentLanguage: PropTypes.object.isRequired,
  updateFilters: PropTypes.func.isRequired,
  syncFilters: PropTypes.func.isRequired,
}

function mapStateToProps() {
  return {}
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    updateFilters: updateAction,
    syncFilters: syncFiltersAction,
  }, dispatch)
}

export default injectIntl(
  connect(mapStateToProps, mapDispatchToProps)(CellarEventsFilter)
)
